import './styles/main.css';
import './styles/components.css';

import { animate } from 'animejs';
import { renderProjectCard, type Project } from './components/projectCard.ts';
import { observeElements } from './animations/scrollObserver.ts';
import { animateReveal } from './animations/pathDrawing.ts';
import { openScrollModal, closeScrollModal } from './animations/scrollAnimation.ts';

export const name = "Victor Wu";
export const title = `Engineering Student at <img src="/ubc-crest.png" alt="UBC" class="ubc-crest-inline" />`;
export const description = "A place for the projects and other things I've made. First year, still learning.";
export const githubUrl = "https://github.com/vlwu";
export const linkedinUrl = "https://linkedin.com/in/victorlwu";
export const emailAddress = "vwu1@student.ubc.ca";

export type ScrollType = 'projects' | 'experience' | 'about';

export const PROJECTS: Project[] = [
  {
    index: "01",
    title: "Parkour Hero",
    tags: ["JavaScript", "HTML5 Canvas", "CSS", "GLSL", "Chrome Extension"],
    year: "2026",
    desc: "A fast-paced 2D parkour platformer built as a Chrome Extension. Players jump, dash, and climb through levels designed around simple movement mechanics. Built primarily in vanilla JS with a custom game loop and collision system, with GLSL vertex and fragment shaders for visual effects. Focused on tight, responsive controls and lightweight performance suitable for running directly in the browser.",
    github: "https://github.com/vlwu/Parkour-Hero",
    live: "https://chromewebstore.google.com/detail/hlplpklcpepphkfjgaegefdiabhpckkm?authuser=1&hl=en-US"
  },
  {
    index: "02",
    title: "Zimu",
    tags: ["Next.js", "TypeScript", "Gemini API", "Firebase"],
    year: "2026",
    desc: "A Chinese reading app that generates short stories calibrated to your exact HSK vocabulary level. Tracks your known-word list and prompts Gemini to write stories built mostly from words you already know, with a handful of new ones at the edge of your ability. Generated text is segmented and validated against your vocabulary before it's shown, and tapping any word surfaces its pinyin and definition in place.",
    github: "https://github.com/vlwu/Zimu",
    live: "https://zimu-reading.vercel.app"
  }
];

export const SKILLS: string[] = [
  "Programming Fundamentals",
  "Data Structures & Algorithms",
  "Web Development",
  "Data Analysis",
  "Teamwork & Collaboration"
];

function inkDivider(colorVar = 'var(--accent-jade)', width = '4') {
  return `
    <div class="ink-divider-container">
      <svg class="ink-divider" viewBox="0 0 1000 20" preserveAspectRatio="none" aria-hidden="true">
        <line x1="2%" y1="10" x2="98%" y2="10" stroke="${colorVar}" stroke-width="${width}" filter="url(#ink-stroke-filter)" />
      </svg>
    </div>
  `;
}

function renderProjectsContent(): string {
  return `
    <div class="scroll-content-inner">
      <p class="section-meta">卷一 · 见闻</p>
      <h2 class="section-heading">Selected Projects</h2>
      ${inkDivider('var(--accent-jade)', '4')}
      <div class="projects-grid">
        ${PROJECTS.map(
          (project) => `
          <div>
            ${renderProjectCard(project)}
            ${inkDivider('var(--muted-neutral)', '2')}
          </div>
        `
        ).join('')}
      </div>
    </div>
  `;
}

function renderExperienceContent(): string {
  return `
    <div class="scroll-content-inner">
      <p class="section-meta">卷二 · 履历</p>
      <h2 class="section-heading">Experience</h2>
      ${inkDivider('var(--accent-jade)', '4')}
      <div class="empty-state">
        <p class="empty-state-text">Nothing here yet.</p>
      </div>
    </div>
  `;
}

function renderAboutContent(): string {
  return `
    <div class="scroll-content-inner">
      <div class="about-grid">
        <div>
          <p class="section-meta">卷三 · 自述</p>
          <h2 class="about-lead">This site is just a place to keep the projects and other things I've done.</h2>
          ${inkDivider('var(--accent-jade)', '4')}
          <div class="about-text-content">
            <p>
              I'm a first-year engineering student at UBC. I mostly build things I want for myself or that seem interesting, and learn whatever I need along the way.
            </p>
            <p>
              Lately that's meant working with APIs and databases. I'm curious about how apps handle user data at scale, and about ways to use AI that go beyond a chatbot wrapper. I haven't settled on a favorite stack yet.
            </p>
          </div>
        </div>

        <div>
          <div style="margin-bottom: 32px;">
            <p class="section-meta" style="color: var(--grey-details);">Expertise</p>
            ${SKILLS.map((skill) => `<div class="skill-row">${skill}</div>`).join('')}
          </div>
          <div>
            <p class="section-meta" style="color: var(--grey-details);">Education</p>
            <p class="edu-degree">BASc</p>
            <p class="edu-location">UBC · Class of 2030</p>
          </div>
        </div>
      </div>
    </div>
  `;
}

// Orchestrates the Handscroll Unfurl (Concept 1) Loading Transition
function setupHandscrollLoader(onComplete: () => void): void {
  const loaderEl = document.getElementById('page-loader');
  const parchmentEl = document.getElementById('loader-parchment');
  const inkStrokeEl = document.getElementById('loader-ink-stroke');
  const sealEl = document.getElementById('loader-cinnabar-seal');

  if (!loaderEl || !parchmentEl || !inkStrokeEl || !sealEl) {
    onComplete();
    return;
  }

  const targetWidth = Math.min(window.innerWidth * 0.55, 230);
  let isReadyTriggered = false;

  // 1. Initial unfurl animation (0 -> ~80% width while assets initialize)
  animate(parchmentEl, {
    width: [0, targetWidth * 0.78],
    duration: 650,
    ease: 'outQuad',
  });

  animate(inkStrokeEl, {
    width: ['0%', '75%'],
    duration: 650,
    ease: 'outQuad',
  });

  const finishLoading = async () => {
    if (isReadyTriggered) return;
    isReadyTriggered = true;

    // 2. Complete the unfurl and ink line
    await Promise.all([
      animate(parchmentEl, {
        width: [parchmentEl.offsetWidth, targetWidth],
        duration: 280,
        ease: 'outCubic',
      }),
      animate(inkStrokeEl, {
        width: [inkStrokeEl.style.width || '75%', '100%'],
        duration: 280,
        ease: 'outCubic',
      }),
    ]);

    // 3. Stamp the cinnabar seal
    sealEl.classList.add('is-stamped');

    // 4. Brief pause to show the stamp before dissolving into the hero
    await new Promise((resolve) => setTimeout(resolve, 320));

    loaderEl.classList.add('is-loaded');

    await new Promise((resolve) => setTimeout(resolve, 500));
    loaderEl.style.display = 'none';

    onComplete();
  };

  // Listen for Shan-Shui ready signal from iframe
  const handleMessage = (e: MessageEvent) => {
    if (e.data && e.data.type === 'shanshui-ready') {
      window.removeEventListener('message', handleMessage);
      finishLoading();
    }
  };
  window.addEventListener('message', handleMessage);

  // Safety fallback: ensure page is revealed even if iframe message is delayed
  setTimeout(() => {
    window.removeEventListener('message', handleMessage);
    finishLoading();
  }, 1400);
}

function bootstrap() {
  const app = document.querySelector<HTMLDivElement>('#app');
  if (!app) return;

  app.innerHTML = `
    <!-- Hero Section (Strictly Viewport Anchored) -->
    <main id="hero" class="hero-section">
      <div class="reveal">
        <p class="hero-label">Portfolio</p>
      </div>

      <div class="reveal">
        <h1 class="hero-name">${name}</h1>
      </div>

      <div class="reveal">
        <h2 class="hero-title">${title}</h2>
      </div>

      <div class="reveal">
        <div class="hero-bio-container">
          <p class="hero-bio">${description}</p>
        </div>
      </div>

      <div class="reveal">
        <div class="socials-container">
          <a href="${githubUrl}" target="_blank" rel="noopener noreferrer" class="social-text-link">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M12 2C6.477 2 2 6.484 2 12.021c0 4.428 2.865 8.184 6.839 9.504.5.092.682-.217.682-.482 0-.237-.009-.868-.013-1.703-2.782.605-3.369-1.342-3.369-1.342-.454-1.154-1.11-1.462-1.11-1.462-.908-.62.069-.608.069-.608 1.003.07 1.532 1.032 1.532 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844a9.59 9.59 0 012.504.338c1.909-1.296 2.747-1.026 2.747-1.026.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.748 0 .268.18.58.688.482C19.138 20.2 22 16.447 22 12.021 22 6.484 17.523 2 12 2z" />
            </svg>
            GitHub
          </a>
          <span class="pipe-divider">|</span>
          <a href="${linkedinUrl}" target="_blank" rel="noopener noreferrer" class="social-text-link">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
            </svg>
            LinkedIn
          </a>
          <span class="pipe-divider">|</span>
          <button id="email-copy-btn" data-email="${emailAddress}" class="social-text-link" aria-label="Copy email address ${emailAddress} to clipboard">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M2 4h20a2 2 0 012 2v12a2 2 0 01-2 2H2a2 2 0 01-2-2V6a2 2 0 012-2zm0 2v.511l10 7.5 10-7.5V6H2zm20 2.657l-9.386 7.04a1 1 0 01-1.228 0L2 8.657V18h20V8.657z" />
            </svg>
            <span class="btn-text" role="status" aria-live="polite">Email</span>
          </button>
        </div>
      </div>

      <!-- Bookshelf of Scrolls -->
      <div class="reveal">
        <section class="shelf-wrapper" aria-label="Archive Scrolls">
          <span class="shelf-hint">Select a Scroll to Unfurl</span>
          <div class="bookshelf">
            <!-- Scroll I: Projects -->
            <button class="shelf-scroll-btn" data-scroll="projects" aria-label="Open Selected Projects scroll">
              <div class="rolled-scroll-cylinder">
                <div class="scroll-rod-core">
                  <div class="finial-knob"></div>
                  <div class="finial-knob"></div>
                </div>
                <div class="scroll-paper-bundle">
                  <div class="brocade-wrap"></div>
                  <div class="silk-ribbon"></div>
                </div>
              </div>
              <div class="scroll-label-group">
                <span class="subtle-seal">卷一</span>
                <span class="scroll-name">Projects</span>
              </div>
            </button>

            <!-- Scroll II: Experience -->
            <button class="shelf-scroll-btn" data-scroll="experience" aria-label="Open Experience scroll">
              <div class="rolled-scroll-cylinder">
                <div class="scroll-rod-core">
                  <div class="finial-knob"></div>
                  <div class="finial-knob"></div>
                </div>
                <div class="scroll-paper-bundle">
                  <div class="brocade-wrap"></div>
                  <div class="silk-ribbon"></div>
                </div>
              </div>
              <div class="scroll-label-group">
                <span class="subtle-seal">卷二</span>
                <span class="scroll-name">Experience</span>
              </div>
            </button>

            <!-- Scroll III: About -->
            <button class="shelf-scroll-btn" data-scroll="about" aria-label="Open About scroll">
              <div class="rolled-scroll-cylinder">
                <div class="scroll-rod-core">
                  <div class="finial-knob"></div>
                  <div class="finial-knob"></div>
                </div>
                <div class="scroll-paper-bundle">
                  <div class="brocade-wrap"></div>
                  <div class="silk-ribbon"></div>
                </div>
              </div>
              <div class="scroll-label-group">
                <span class="subtle-seal">卷三</span>
                <span class="scroll-name">About</span>
              </div>
            </button>

            <div class="bookshelf-rack-line" aria-hidden="true"></div>
          </div>
        </section>
      </div>
    </main>

    <!-- Vertical Hanging Scroll Modal -->
    <div id="scroll-modal" class="scroll-modal" aria-hidden="true" role="dialog" aria-modal="true">
      <div class="scroll-modal-backdrop"></div>
      
      <div class="hanging-scroll-wrapper">
        <!-- Close Cinnabar Stamp -->
        <button id="modal-close-seal" class="scroll-close-seal" aria-label="Roll up and close scroll">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>

        <!-- Hanging Silk Cord & Ring -->
        <div class="scroll-suspension" aria-hidden="true">
          <div class="suspension-ring"></div>
          <div class="suspension-braid"></div>
        </div>

        <!-- Top Stave Rod -->
        <div class="scroll-rod">
          <div class="rod-finial"></div>
          <div class="rod-finial"></div>
        </div>

        <!-- Top Silk Brocade Border -->
        <div class="scroll-silk-border" aria-hidden="true"></div>

        <!-- Parchment Window with Internal Scroll -->
        <div class="scroll-window">
          <div id="scroll-paper-content" class="scroll-inner-paper"></div>
        </div>

        <!-- Bottom Silk Brocade Border -->
        <div class="scroll-silk-border" aria-hidden="true"></div>

        <!-- Bottom Weighted Roller Rod -->
        <div class="scroll-rod">
          <div class="rod-finial"></div>
          <div class="rod-finial"></div>
        </div>
      </div>
    </div>
  `;

  // Start the Handscroll Unfurl loader transition, then trigger entrance reveals
  setupHandscrollLoader(() => {
    observeElements('.reveal', (el) => animateReveal(el));
  });

  // Email Copy with fallback
  const emailBtn = document.getElementById('email-copy-btn');
  if (emailBtn) {
    const email = emailBtn.getAttribute('data-email') || '';
    const textSpan = emailBtn.querySelector('.btn-text');
    let copyTimeout: number | undefined;

    emailBtn.addEventListener('click', async () => {
      try {
        await navigator.clipboard.writeText(email);
        emailBtn.classList.add('copied');
        if (textSpan) textSpan.textContent = 'Copied!';
        if (copyTimeout) clearTimeout(copyTimeout);
        copyTimeout = window.setTimeout(() => {
          emailBtn.classList.remove('copied');
          if (textSpan) textSpan.textContent = 'Email';
        }, 2000);
      } catch {
        window.location.href = `mailto:${email}`;
      }
    });
  }

  // Scroll Modal Orchestration
  const modalEl = document.getElementById('scroll-modal') as HTMLElement;
  const contentEl = document.getElementById('scroll-paper-content') as HTMLElement;
  const closeBtn = document.getElementById('modal-close-seal');
  const backdrop = modalEl?.querySelector('.scroll-modal-backdrop');
  let lastActiveButton: HTMLButtonElement | null = null;

  const handleOpenScroll = (type: ScrollType, sourceBtn: HTMLButtonElement) => {
    lastActiveButton = sourceBtn;
    if (type === 'projects') {
      contentEl.innerHTML = renderProjectsContent();
    } else if (type === 'experience') {
      contentEl.innerHTML = renderExperienceContent();
    } else {
      contentEl.innerHTML = renderAboutContent();
    }
    contentEl.scrollTop = 0;
    openScrollModal(modalEl);
  };

  const handleCloseScroll = () => {
    closeScrollModal(modalEl).then(() => {
      lastActiveButton?.focus();
    });
  };

  document.querySelectorAll<HTMLButtonElement>('.shelf-scroll-btn').forEach((btn) => {
    btn.addEventListener('click', () => {
      const type = btn.getAttribute('data-scroll') as ScrollType;
      if (type) handleOpenScroll(type, btn);
    });
  });

  closeBtn?.addEventListener('click', handleCloseScroll);
  backdrop?.addEventListener('click', handleCloseScroll);

  window.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && modalEl?.classList.contains('is-open')) {
      handleCloseScroll();
    }
  });

  // Mouse Parallax Bridge to Shan-Shui Iframe (Rate-limited to 30Hz)
  let lastParallaxTime = 0;
  let lastNormX = 0;
  let lastNormY = 0;

  window.addEventListener('mousemove', (e) => {
    if (modalEl?.classList.contains('is-open')) return;

    const now = performance.now();
    if (now - lastParallaxTime < 32) return;

    const normX = (e.clientX / window.innerWidth) * 2 - 1;
    const normY = (e.clientY / window.innerHeight) * 2 - 1;

    if (Math.abs(normX - lastNormX) < 0.015 && Math.abs(normY - lastNormY) < 0.015) {
      return;
    }

    lastParallaxTime = now;
    lastNormX = normX;
    lastNormY = normY;

    const iframe = document.getElementById('shanshui-bg') as HTMLIFrameElement | null;
    iframe?.contentWindow?.postMessage({ type: 'shanshui-mouse', x: normX, y: normY }, '*');
  }, { passive: true });
}

document.addEventListener('DOMContentLoaded', bootstrap);